import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Tab, Tabs } from 'react-bootstrap';
import _orderBy from 'lodash/orderBy';
import _map from 'lodash/map';
import _groupBy from 'lodash/groupBy';
import _throttle from 'lodash/throttle';
import memoizeOne from 'memoize-one';
import moment from 'moment';
import Loader from './svg/Loader';
import * as BookingActions from '../actions/booking';
import * as PopupActions from '../actions/popup';
import BookingHistoryDetails from './partials/BookingHistoryDetails';
import { getOperatingSystem } from '../utils/utils';
import { isPrimaryColorDarkSelector, refreshBookingsFlagSelector } from '../helpers/selectors';

const TAB_KEYS = {
  UPCOMING: '1',
  PAST: '2'
};
const DEFAULT_PAGE_SIZE = 10;

class BookingHistory extends Component {
  constructor(props) {
    super(props);
    this.state = {
      upcomingPage: 0,
      finishedPage: 0,
      loadingUpcoming: false,
      loadingFinished: false,
      hasMoreUpcoming: true,
      hasMoreFinished: true,
      activeTab: TAB_KEYS.UPCOMING,
      tabsLoaded: {
        upcoming: false,
        past: false
      }
    };

    this.browserOs = getOperatingSystem();

    this.handleLoadMoreUpcoming = this.handleLoadMoreUpcoming.bind(this);
    this.handleLoadMoreFinished = this.handleLoadMoreFinished.bind(this);
    this.handleTabChange = this.handleTabChange.bind(this);
    this.showBookingCancelPopup = this.showBookingCancelPopup.bind(this);
    this.showBookingUpdatePopup = this.showBookingUpdatePopup.bind(this);

    this.memoizeCallbacks();
  }

  componentDidMount() {
    this.loadInitialBookings();
    if (this.props.refreshBookingsFlag) {
      this.props.clearRefreshBookingsFlag();
      this.refreshCurrentTab();
    }
  }

  componentDidUpdate(prevProps) {
    const wasOnEditPage = prevProps.location?.pathname?.includes('edit-booking');
    const nowOnBookingsPage = this.props.location?.pathname === '/my-bookings';

    if (wasOnEditPage && nowOnBookingsPage) {
      this.refreshCurrentTab();
    }

    if (this.props.refreshBookingsFlag && !prevProps.refreshBookingsFlag) {
      this.props.clearRefreshBookingsFlag();
      this.refreshCurrentTab();
    }
  }

  refreshCurrentTab() {
    const { activeTab } = this.state;

    if (activeTab === TAB_KEYS.UPCOMING) {
      this.props.clearBookings('upcoming');
      this.setState({ upcomingPage: 0, hasMoreUpcoming: true }, () => {
        this.loadUpcomingBookings(1, DEFAULT_PAGE_SIZE, ['UPCOMING']);
      });
    }
  }

  memoizeCallbacks() {
    // Utiliser une seule instance memoized pour les deux types
    const memoSorted = memoizeOne(this.getSortedBookings);

    this.getUpcomingGroupedBookings = () =>
      memoSorted(this.props.booking.myUpcomingBookings || [], 'upcoming');

    this.getFinishedGroupedBookings = () =>
      memoSorted(this.props.booking.myFinishedBookings || [], 'past');
  }

  loadInitialBookings() {
    const alreadyLoaded = this.props.booking?.myUpcomingBookings?.length > 0;

    if (!alreadyLoaded) {
      this.loadUpcomingBookings(1, DEFAULT_PAGE_SIZE, ['UPCOMING']);
      this.setState((prevState) => ({
        tabsLoaded: { ...prevState.tabsLoaded, upcoming: true }
      }));
    } else {
      // Mark the tab as loaded to avoid re-fetching on tab change
      this.setState((prevState) => ({
        tabsLoaded: { ...prevState.tabsLoaded, upcoming: true },
        upcomingPage: this.props.booking.upcoming?.currentPage || 1,
        hasMoreUpcoming: this.props.booking.upcoming?.hasMore ?? true
      }));
    }
  }

  handleTabChange(key) {
    this.setState({ activeTab: key }, () => {
      const { tabsLoaded } = this.state;

      if (key === TAB_KEYS.PAST && !tabsLoaded.past) {
        const size = this.props.booking.past?.pageSize || DEFAULT_PAGE_SIZE;

        if (this.props.booking.past.page === 1) {
          const _page = this.props.booking.past.page;

          this.loadFinishedBookings(_page, size, ['PAST', 'CANCELED']).then(() => {
            this.setState((prev) => ({
              tabsLoaded: { ...prev.tabsLoaded, past: true }
            }));
          });
        }
      }

      if (key === TAB_KEYS.UPCOMING && !tabsLoaded.upcoming) {
        const size = this.props.booking.upcoming?.pageSize || DEFAULT_PAGE_SIZE;

        this.loadUpcomingBookings(1, size, ['UPCOMING']).then(() => {
          this.setState((prev) => ({
            tabsLoaded: { ...prev.tabsLoaded, upcoming: true }
          }));
        });
      }
    });
  }

  getSortedBookings(bookings, bookingsType) {
    if (!bookings || bookings.length === 0) return [];

    const grouped = _groupBy(bookings, (b) => {
      const sharingInfo = b.carSharingInfo || b.rrsSharingInfo;
      const date = sharingInfo?.effectiveStartDate || b.start?.date;

      return date ? moment(date).format('YYYY-MM-DD') : '';
    });

    const mapped = _map(grouped, (group, day) => ({ day, grouped: group }));

    return bookingsType === 'upcoming'
      ? _orderBy(mapped, ['day'], ['asc'])
      : _orderBy(mapped, ['day'], ['desc']);
  }

  loadUpcomingBookings(page, size, states) {
    const { loadingUpcoming, hasMoreUpcoming } = this.state;

    if (loadingUpcoming || !hasMoreUpcoming) return Promise.resolve();

    this.setState({ loadingUpcoming: true });
    return this.props
      .getMemberBookingsPage(page, size, states)
      .then((resp) => {
        this.setState({
          upcomingPage: page,
          loadingUpcoming: false,
          hasMoreUpcoming: resp.results.length >= size
        });
      })
      .catch(() => {
        this.setState({ loadingUpcoming: false });
      });
  }

  loadFinishedBookings(page, size, states) {
    if (this.state.loadingFinished || !this.state.hasMoreFinished) {
      return Promise.resolve();
    }

    this.setState({ loadingFinished: true });
    return this.props
      .getMemberBookingsPage(page, size, states)
      .then((resp) => {
        const currentPage = resp.pageInfo.currentPage;
        const totalPages = resp.pageInfo.totalPages;
        const hasMore = currentPage < totalPages;

        this.setState({
          finishedPage: page,
          loadingFinished: false,
          hasMoreFinished: hasMore
        });
      })
      .catch((e) => {
        this.setState({ loadingFinished: false });
      });
  }

  handleLoadMoreUpcoming() {
    const size = this.props.booking.upcoming?.pageSize || DEFAULT_PAGE_SIZE;

    this.loadUpcomingBookings(this.state.upcomingPage + 1, size, ['UPCOMING']);
  }

  handleLoadMoreFinished() {
    const size = this.props.booking.past?.pageSize || DEFAULT_PAGE_SIZE;

    this.loadFinishedBookings(this.state.finishedPage + 1, size, ['PAST', 'CANCELED']);
  }

  showBookingCancelPopup(bookingId) {
    this.props.loadBookingCancelId(bookingId);
    this.props.popup_BookingCancel_SetVisibility(true);
  }

  showBookingUpdatePopup(bookingData) {
    this.props.loadBookingEditPopupData(bookingData);
    this.props.popup_BookingEdit_SetVisibility(true);
  }

  pageInfo(type) {
    const { booking } = this.props;
    const { loadingFinished, loadingUpcoming } = this.state;
    const { past, upcoming, myFinishedBookings, myUpcomingBookings } = booking;

    if (type === 'upcoming') {
      return (
        <div className="pager">
          {loadingUpcoming && <Loader className="svg-loader" color={'#000'} />}
          {myUpcomingBookings.length > 0 && (
            <span className="page-info">
              <FormattedMessage
                id="label.bookings.pagination"
                values={{
                  current: myUpcomingBookings.length,
                  total: upcoming.totalResults
                }}
              />
            </span>
          )}
        </div>
      );
    }
    if (type === 'past') {
      return (
        <div className="pager">
          {loadingFinished && <Loader className="svg-loader" color={'#000'} />}
          {myFinishedBookings.length > 0 && (
            <span className="page-info">
              <FormattedMessage
                id="label.bookings.pagination"
                values={{
                  current: myFinishedBookings.length,
                  total: past.totalResults
                }}
              />
            </span>
          )}
        </div>
      );
    }
  }

  render() {
    const { activeTab } = this.state;
    const { useWhiteIcons } = this.props;

    return (
      <div className="booking-history">
        <div className="booking-history-padding">
          <div className="booking-history-title">
            <FormattedMessage id="label.my_reservations" />
          </div>
          <Tabs
            id="booking-history-tabs"
            className="booking-history-tabs"
            activeKey={activeTab}
            onSelect={this.handleTabChange}
          >
            <Tab
              eventKey={TAB_KEYS.UPCOMING}
              title={<FormattedMessage id="generic.upcoming" />}
            >
              {activeTab === TAB_KEYS.UPCOMING && (
                <>
                  {this.pageInfo('upcoming')}
                  <BookingHistoryDetails
                    bookingsType="upcoming"
                    browserOs={this.browserOs}
                    useWhiteIcons={useWhiteIcons}
                    groupedBookings={this.getUpcomingGroupedBookings()}
                    cancelAction={this.showBookingCancelPopup}
                    updateAction={this.showBookingUpdatePopup}
                    loadMore={this.handleLoadMoreUpcoming}
                    hasMore={this.state.hasMoreUpcoming}
                    loading={this.state.loadingUpcoming}
                  />
                </>
              )}
            </Tab>
            <Tab
              eventKey={TAB_KEYS.PAST}
              title={<FormattedMessage id="label.booking.past" />}
            >
              <>
                {this.pageInfo('past')}
                {activeTab === TAB_KEYS.PAST && (
                  <BookingHistoryDetails
                    bookingsType="past"
                    browserOs={this.browserOs}
                    useWhiteIcons={useWhiteIcons}
                    groupedBookings={this.getFinishedGroupedBookings()}
                    loadMore={this.handleLoadMoreFinished}
                    hasMore={this.state.hasMoreFinished}
                    loading={this.state.loadingFinished}
                  />
                )}
              </>
            </Tab>
          </Tabs>
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    booking: state.booking,
    popup: state.popup,
    useWhiteIcons: isPrimaryColorDarkSelector(state),
    refreshBookingsFlag: refreshBookingsFlagSelector(state)
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      ...BookingActions,
      ...PopupActions
    },
    dispatch
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(BookingHistory);
